import React, { useState, useEffect } from 'react';
import { X, Users, Target, Plus, Trash2, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useApiClient } from '../services/api';
import './TeamRegistrationForm.css';

interface TeamRegistrationFormProps {
  eventId: string;
  eventTitle: string;
  onSubmit: (data: TeamRegistrationData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  isEditMode?: boolean;
  teamId?: string;
  onUnregister?: () => void;
  isUnregistering?: boolean;
  isFullPage?: boolean;
}

export interface TeamRegistrationData {
  teamName: string;
  eventId: string;
  registrationType: 'team' | 'solo' | 'matchmaking';
  lookingForTeam?: boolean;
  lookingForSkills: string[];
  teamMemberEmails: string[];
  status?: 'Solo' | 'Looking for a Team' | 'Has Team'; // Three status values supported in Notion
}

const SKILL_OPTIONS = [
  'Frontend Development',
  'Backend Development',
  'Full Stack Development',
  'UI/UX Design',
  'Mobile Development',
  'Data Science',
  'Machine Learning',
  'DevOps',
  'Blockchain',
  'Smart Contracts',
  'Product Management',
  'Marketing',
  'Business Development',
];

export const TeamRegistrationForm: React.FC<TeamRegistrationFormProps> = ({
  eventId,
  eventTitle,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isEditMode = false,
  teamId,
  onUnregister,
  isUnregistering = false,
  isFullPage = false,
}) => {
  const [registrationType, setRegistrationType] = useState<'team' | 'solo' | 'matchmaking'>('team');
  const [teamName, setTeamName] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [teamMemberEmails, setTeamMemberEmails] = useState<string[]>(['']);
  const [customSkill, setCustomSkill] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingTeamData, setLoadingTeamData] = useState(false);
  const apiClient = useApiClient();

  // Autocomplete state
  const [autocompleteResults, setAutocompleteResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [showAutocomplete, setShowAutocomplete] = useState<number | null>(null);
  const [searchingParticipants, setSearchingParticipants] = useState(false);

  // Fetch and populate team data when in edit mode
  useEffect(() => {
    const fetchTeamData = async () => {
      if (isEditMode && teamId) {
        setLoadingTeamData(true);
        try {
          const response = await apiClient.checkUserTeamForEvent(eventId);

          if (response.success && response.data?.team) {
            const team = response.data.team;

            // Populate form fields with existing team data
            // Handle both camelCase (Notion) and snake_case (Supabase) field names
            const teamName = team.teamName || team.team_name || '';
            if (teamName) {
              setTeamName(teamName);
            }

            // Determine registration type based on team status
            const status = team.status || team.Status || 'Solo';
            if (status === 'Solo') {
              setRegistrationType('solo');
            } else if (status === 'Looking for a Team') {
              setRegistrationType('matchmaking');
            } else {
              setRegistrationType('team');
            }

            if (team.lookingForSkills && Array.isArray(team.lookingForSkills)) {
              setSelectedSkills(team.lookingForSkills);
            } else if (team.looking_for_skills && Array.isArray(team.looking_for_skills)) {
              setSelectedSkills(team.looking_for_skills);
            }

            // Fetch team member emails
            const memberIds = team.has_team_member_ids || team.hasTeamMembers || [];
            if (Array.isArray(memberIds) && memberIds.length > 0) {
              try {
                const emails = await Promise.all(
                  memberIds.map(async (id: string) => {
                    const userRes = await apiClient.getUserById(id);
                    return userRes.success && userRes.data ? userRes.data.email : null;
                  })
                );
                const validEmails = emails.filter((e): e is string => !!e);
                if (validEmails.length > 0) {
                  setTeamMemberEmails(validEmails);
                }
              } catch (err) {
                console.error('Error fetching team member emails:', err);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching team data:', err);
        } finally {
          setLoadingTeamData(false);
        }
      }
    };

    fetchTeamData();
  }, [isEditMode, teamId, eventId, apiClient]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Team name is only required for team registration
    if (registrationType === 'team') {
      if (!teamName.trim()) {
        newErrors.teamName = 'Team name is required';
      } else if (teamName.trim().length < 3) {
        newErrors.teamName = 'Team name must be at least 3 characters';
      }
    }

    // Validate emails if provided (only for team registration)
    if (registrationType === 'team') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      teamMemberEmails.forEach((email, index) => {
        if (email.trim() && !emailRegex.test(email.trim())) {
          newErrors[`email_${index}`] = 'Invalid email format';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const data: TeamRegistrationData = {
      teamName: registrationType === 'team' ? teamName.trim() : '',
      eventId,
      registrationType,
      lookingForTeam: registrationType === 'matchmaking' ? true : false,
      lookingForSkills: (registrationType === 'team' || registrationType === 'matchmaking') ? selectedSkills : [],
      teamMemberEmails: registrationType === 'team' ? teamMemberEmails.filter(email => email.trim() !== '') : [],
      status: registrationType === 'team' ? 'Has Team' : (registrationType === 'matchmaking' ? 'Looking for a Team' : 'Solo'),
    };

    await onSubmit(data);
  };

  const toggleSkill = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const addCustomSkill = () => {
    if (customSkill.trim() && !selectedSkills.includes(customSkill.trim())) {
      setSelectedSkills([...selectedSkills, customSkill.trim()]);
      setCustomSkill('');
    }
  };

  const addEmailField = () => {
    setTeamMemberEmails([...teamMemberEmails, '']);
  };

  const removeEmailField = (index: number) => {
    setTeamMemberEmails(teamMemberEmails.filter((_, i) => i !== index));
  };

  const updateEmailField = async (index: number, value: string) => {
    const updated = [...teamMemberEmails];
    updated[index] = value;
    setTeamMemberEmails(updated);

    // Trigger autocomplete search if value is at least 2 characters
    if (value.length >= 2) {
      setShowAutocomplete(index);
      setSearchingParticipants(true);
      try {
        const response = await apiClient.searchParticipants(value, eventId);
        if (response.success && response.data) {
          setAutocompleteResults(response.data);
          console.log('Autocomplete results:', response.data);
        }
      } catch (error) {
        console.error('Error searching participants:', error);
      } finally {
        setSearchingParticipants(false);
      }
    } else {
      setShowAutocomplete(null);
      setAutocompleteResults([]);
    }
  };

  const selectParticipant = (index: number, email: string) => {
    const updated = [...teamMemberEmails];
    updated[index] = email;
    setTeamMemberEmails(updated);
    setShowAutocomplete(null);
    setAutocompleteResults([]);
  };

  const formContent = (
    <Card className={isFullPage ? "w-full" : "team-registration-card"}>
      <CardHeader className={isFullPage ? "p-6" : "team-registration-header"}>
        <div className={isFullPage ? "flex items-start justify-between" : "header-content"}>
          <div className={isFullPage ? "flex-1" : "header-info"}>
            <p className={isFullPage ? "text-lg text-gray-600 mb-2" : "event-name"}>{eventTitle}</p>
            <CardTitle className={isFullPage ? "text-3xl" : "form-title"}>{isEditMode ? 'Modify Team' : 'Register for Event'}</CardTitle>
          </div>
          {!isFullPage && (
            <button
              type="button"
              onClick={onCancel}
              className="close-button"
              disabled={isSubmitting || loadingTeamData}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </CardHeader>

      <CardContent className={isFullPage ? "p-6" : "team-registration-content"}>
        <form onSubmit={handleSubmit} className="registration-form">
          {/* Registration Type Selection */}
          <div className="form-group">
            <label className={isFullPage && isEditMode ? "form-label font-semibold text-gray-900" : "form-label"}>
              {isFullPage && isEditMode ? 'Registration Type' : 'Registration Type *'}
            </label>
            <div className="registration-type-selector">
              <button
                type="button"
                className={`type-button ${registrationType === 'team' ? 'active' : ''}`}
                onClick={() => setRegistrationType('team')}
                disabled={isSubmitting || loadingTeamData}
              >
                <Users className="h-4 w-4" />
                <span>Create a Team</span>
              </button>
              <button
                type="button"
                className={`type-button ${registrationType === 'solo' ? 'active' : ''}`}
                onClick={() => setRegistrationType('solo')}
                disabled={isSubmitting || loadingTeamData}
              >
                <Target className="h-4 w-4" />
                <span>Join Solo</span>
              </button>
              <button
                type="button"
                className={`type-button ${registrationType === 'matchmaking' ? 'active' : ''}`}
                onClick={() => setRegistrationType('matchmaking')}
                disabled={isSubmitting || loadingTeamData}
              >
                <Users className="h-4 w-4" />
                <span>Matchmaking</span>
              </button>
            </div>
          </div>

          {/* Team Name - Only show for team registration */}
          {registrationType === 'team' && (
            <div className="form-group">
              <label htmlFor="teamName" className={isFullPage && isEditMode ? "form-label font-semibold text-gray-900 flex items-center gap-2" : "form-label"}>
                <Users className="h-4 w-4" />
                {isFullPage && isEditMode ? 'Team Name' : 'Team Name *'}
              </label>
              <input
                id="teamName"
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Enter your team name"
                className={`form-input ${isFullPage && isEditMode ? 'border-transparent focus:border-purple-300 bg-gray-50 hover:bg-white transition-colors' : ''} ${errors.teamName ? 'input-error' : ''}`}
                disabled={isSubmitting}
                maxLength={100}
              />
              {errors.teamName && (
                <span className="error-message">{errors.teamName}</span>
              )}
            </div>
          )}

          {/* Looking for Skills - Show for team OR matchmaking */}
          {(registrationType === 'team' || registrationType === 'matchmaking') && (
            <div className="form-group">
              <label className={isFullPage && isEditMode ? "form-label font-semibold text-gray-900 flex items-center gap-2" : "form-label"}>
                <Target className="h-4 w-4" />
                {registrationType === 'matchmaking' ? 'Skills You Have/Want' : 'Looking for Skills'}
                {!(isFullPage && isEditMode) && ' (Optional)'}
                {isFullPage && isEditMode && (
                  <div className="group relative inline-block">
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                    <span className="invisible group-hover:visible absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                      {registrationType === 'matchmaking'
                        ? 'Select skills you have or are looking for in teammates'
                        : 'Select skills you\'re looking for in team members'}
                    </span>
                  </div>
                )}
              </label>
              {!(isFullPage && isEditMode) && (
                <p className="form-hint">
                  {registrationType === 'matchmaking'
                    ? 'Select skills you have or are looking for in teammates'
                    : 'Select skills you\'re looking for in team members'}
                </p>
              )}
              <div className="skills-container">
                {SKILL_OPTIONS.map((skill) => (
                  <Badge
                    key={skill}
                    variant={selectedSkills.includes(skill) ? 'default' : 'outline'}
                    className={`skill-badge ${selectedSkills.includes(skill) ? 'selected' : ''}`}
                    onClick={() => !isSubmitting && toggleSkill(skill)}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>

              {/* Custom Skill Input */}
              <div className="relative">
                <div className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-400 focus-within:border-transparent flex flex-wrap gap-1.5 items-center bg-white">
                  {selectedSkills.filter(skill => !SKILL_OPTIONS.includes(skill)).map((skill, index) => (
                    <span
                      key={skill}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(skill.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => setSelectedSkills(selectedSkills.filter(s => s !== skill))}
                        className="ml-1 hover:opacity-70 transition-opacity"
                        disabled={isSubmitting}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={customSkill}
                    onChange={(e) => setCustomSkill(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === ' ' && customSkill.trim()) {
                        e.preventDefault();
                        addCustomSkill();
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (customSkill.trim()) {
                          addCustomSkill();
                        }
                      } else if (e.key === 'Backspace' && !customSkill) {
                        const customSkills = selectedSkills.filter(skill => !SKILL_OPTIONS.includes(skill));
                        if (customSkills.length > 0) {
                          setSelectedSkills(selectedSkills.filter(s => s !== customSkills[customSkills.length - 1]));
                        }
                      }
                    }}
                    placeholder={selectedSkills.filter(skill => !SKILL_OPTIONS.includes(skill)).length === 0 ? "Type and press space to add custom skills..." : ""}
                    className="flex-1 min-w-[120px] outline-none bg-transparent"
                    disabled={isSubmitting}
                    maxLength={50}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Press space or enter to add a custom skill. Backspace to remove the last custom skill.</p>
              </div>
            </div>
          )}

          {/* Team Member Emails - Only show for team registration */}
          {registrationType === 'team' && (
            <div className="form-group">
              <label className={isFullPage && isEditMode ? "form-label font-semibold text-gray-900 flex items-center gap-2" : "form-label"}>
                <Users className="h-4 w-4" />
                Team Member Emails
                {!(isFullPage && isEditMode) && ' (Optional)'}
                {isFullPage && isEditMode && (
                  <div className="group relative inline-block">
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                    <span className="invisible group-hover:visible absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                      Add emails of team members who are <strong>already registered</strong>
                    </span>
                  </div>
                )}
              </label>
              {!(isFullPage && isEditMode) && (
                <p className="form-hint">
                  Add emails of team members who are <strong>already registered</strong>
                </p>
              )}
              <div className="email-fields">
                {teamMemberEmails.map((email, index) => (
                  <div key={index} className="email-field-row relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmailField(index, e.target.value)}
                      onFocus={() => email.length >= 2 && setShowAutocomplete(index)}
                      onBlur={() => setTimeout(() => setShowAutocomplete(null), 200)}
                      placeholder="teammate@example.com"
                      className={`form-input ${isFullPage && isEditMode ? 'border-transparent focus:border-purple-300 bg-gray-50 hover:bg-white transition-colors' : ''} ${errors[`email_${index}`] ? 'input-error' : ''}`}
                      disabled={isSubmitting}
                      autoComplete="off"
                    />

                    {/* Autocomplete dropdown */}
                    {showAutocomplete === index && autocompleteResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {searchingParticipants ? (
                          <div className="p-3 text-sm text-gray-500 text-center">Searching...</div>
                        ) : (
                          autocompleteResults.map((participant) => (
                            <div
                              key={participant.id}
                              onClick={() => selectParticipant(index, participant.email)}
                              className="px-4 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="text-sm font-medium text-gray-900">{participant.name}</div>
                              <div className="text-xs text-gray-500">{participant.email}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {teamMemberEmails.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        disabled={isSubmitting}
                        variant="outline"
                        size="sm"
                        className="remove-button"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {errors[`email_${index}`] && (
                      <span className="error-message">{errors[`email_${index}`]}</span>
                    )}
                  </div>
                ))}
              </div>
              {teamMemberEmails.length < 10 && (
                <Button
                  type="button"
                  onClick={addEmailField}
                  disabled={isSubmitting}
                  variant="outline"
                  size="sm"
                  className="add-email-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Team Member
                </Button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <Button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting || isUnregistering}
              variant="outline"
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || loadingTeamData || isUnregistering}
              className="submit-button flex-1"
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" />
                  {isEditMode ? 'Updating...' : (registrationType === 'solo' ? 'Registering Solo...' : (registrationType === 'matchmaking' ? 'Joining Matchmaking...' : 'Creating Team...'))}
                </>
              ) : loadingTeamData ? (
                <>
                  <div className="spinner" />
                  Loading...
                </>
              ) : (
                isEditMode ? 'Update Registration' : (registrationType === 'solo' ? 'Register Solo' : (registrationType === 'matchmaking' ? 'Join Matchmaking' : 'Create Team & Register'))
              )}
            </Button>
          </div>

          {/* Unregister Section - Only show in edit mode at the bottom */}
          {isEditMode && onUnregister && isFullPage && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                If you registered by mistake, unregister from the event here.
              </p>
              <Button
                type="button"
                onClick={onUnregister}
                disabled={isSubmitting || isUnregistering || loadingTeamData}
                variant="outline"
                className="border-2 border-red-500 text-red-600 hover:bg-red-50"
              >
                {isUnregistering ? (
                  <>
                    <div className="spinner red" />
                    Unregistering...
                  </>
                ) : (
                  'Unregister from Event'
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );

  // If full page mode, return just the form content
  if (isFullPage) {
    return formContent;
  }

  // Otherwise, wrap in modal overlay
  return (
    <div className="team-registration-overlay">
      <div className="team-registration-modal">
        {formContent}
      </div>
    </div>
  );
};

export default TeamRegistrationForm;

